/**
 * Canonical slug → icon map for the sidebar nav items resolved by
 * `@ordo/access`'s `resolveNavItems()`.
 *
 * Single source shared by the sidebar rows (`OrdoSidebar`) and any page
 * whose header mirrors its nav row (the chef kitchen-list pages) — so a
 * nav item's icon can never diverge between the row and the page it
 * opens. New nav slugs added in `@ordo/access/nav.ts` need an entry
 * here; tests in @ordo/access pin the canonical slugs so a missing
 * icon surfaces immediately.
 *
 * Labels have the same one-source rule via i18n: `nav.<slug>` in
 * `@ordo/i18n`'s message catalog is the display name for both the
 * sidebar row and a page header that mirrors it. A slug with no catalog
 * entry falls back to the server-provided English label.
 */

import {
  Building2,
  Calendar,
  ChefHat,
  ClipboardList,
  ListChecks,
  type LucideIcon,
  Package,
  Receipt,
  ShoppingBasket,
  Users,
} from 'lucide-react'

export const NAV_ICONS = {
  tasks: ListChecks,
  orders: Calendar,
  'my-family': Users,
  // Slug stays 'shopping-list' (stable ID); the row reads "Ingredients"
  // since the Aug 2026 chef-sidebar split.
  'shopping-list': ShoppingBasket,
  recipes: ChefHat,
  deliverables: Package,
  'production-records': ClipboardList,
  billing: Receipt,
  settings: Building2,
} satisfies Record<string, LucideIcon>

export type NavIconSlug = keyof typeof NAV_ICONS
